import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000DeleteHandlers } from "../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import type { U2000TableColumn } from "../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { u2000DefaultSortOrderForColumn } from "../../../u2000Core/tools/U2000DefaultSortOrderForColumn";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../../u2000Core/reducer/formReducer/U2000LUState";
import type { U2000GridStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { useU3129Context } from "../../U3129Context";
import { doDelete, doSearch, luHandleApiError, type U3129Timesheet, type U3129SearchPayload } from "../../U3129Service";
import type { U3129PeriodPreset } from "../../tools/U3129PeriodPresets";
import { getCurrentWeekMondayIsoLocal, getPreviousWeekMondayIsoLocal } from "../../tools/U3129PeriodPresets";
import { NS, U3129ROLES } from "../../U3129";
import U3129AddTimesheetDialog from "../U3129AddTimesheetDialog";
import U3129TimesheetDeleteDlg from "../common/U3129TimesheetDeleteDlg";
import U3129TimesheetListToolBar from "./U3129TimesheetListToolBar";
import { u3129GetTimesheetListColumns } from "../../tools/U3129TimesheetListColumns";
import {
  U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE,
  defaultItemToDisplay,
  defaultItemToSelectedDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import { useU3129FindEmployeeHandlers } from "../../U3129UseFindProjectSubprojectHandlers";

/** Traces DEV pour diagnostiquer décalage critères UI vs payload search (voir runSearchPage1Ref). */
function traceU3129Search(label: string, data: unknown): void {
  if (!import.meta.env.DEV) return;
  console.debug(`[U3129SearchTrace] ${label}`, data);
}

/** Modifier / supprimer autorisés uniquement au statut Open (aligné backend). */
function isU3129TimesheetOpen(row: U3129Timesheet | null | undefined): boolean {
  return row?.status === "Open";
}

function u3129SelectedRowsAllOpen(rows: U3129Timesheet[]): boolean {
  return rows.length > 0 && rows.every((r) => r.status === "Open");
}

const U3129TimesheetList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { initLU, luStateHandlers, searchStateHandlers, gridStateHandlers, employeeId, setEmployeeId, employee, showAllEmployees, setShowAllEmployees, periodPreset, setPeriodPreset, periodPresetInitialized, setPeriodPresetInitialized } =
    useU3129Context();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const didRunInitialListSearchRef = useRef(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const columnCount = searchStateHandlers.getColumns().length;
  const canSearchOtherEmployees = hasRole(U3129ROLES.OTHER_EMPLOYEE);
  const findEmployeeHandlers = useU3129FindEmployeeHandlers();

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE, {
      search: findEmployeeHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
      itemToSelectedDisplay: defaultItemToSelectedDisplay,
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE);
  }, [findEmployeeHandlers.search]);

  useEffect(() => {
    const columns = u3129GetTimesheetListColumns(t);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
    // Colonnes affichées par défaut : initialiser dès le chargement pour afficher les en-têtes même sans enregistrements.
    const currentFields = searchStateHandlers.getFields();
    if (currentFields.length === 0) {
      searchStateHandlers.setFields(columns.filter((col: U2000TableColumn) => col.isDefaultColumn).map((col: U2000TableColumn) => ({ field: { ...col } })));
    }
    const currentSortedFields = searchStateHandlers.getSortedFields();
    if (currentSortedFields.length === 0) {
      searchStateHandlers.setSortedFields(
        columns
          .filter((col: U2000TableColumn) => col.isDefaultSortedColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init colonnes / breadcrumb : éviter re-exécutions sur handlers stables
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U3129Timesheet>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      traceU3129Search("doSearchFn.filter (envoyé au serveur)", searchPayload.filter);
      traceU3129Search("doSearchFn.filter.criterias", searchPayload.filter?.criterias ?? null);
      traceU3129Search("doSearchFn.showAllEmployees", showAllEmployees);
      if (!showAllEmployees && (!employeeId || employeeId.trim() === "")) {
        toast.error(t("msgSelectEmployee", { ns: NS }));
        return {
          data: {
            input: {
              filter: searchPayload.filter,
              pagination: searchPayload.pagination,
            },
            results: [],
            queryRowsCount: 0,
          },
        };
      }
      const payload: U3129SearchPayload = {
        ...searchPayload,
        employeeId: employeeId ?? "",
        showAllEmployees,
      };
      return await doSearch(u2000AxiosAuthenticated, payload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3129Timesheet>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers<U3129Timesheet>({
    getSelectedRows: () => gridStateHandlers.getSelectedRows(),
    doDeleteFn: async (idParam, row) => doDelete(u2000AxiosAuthenticated, idParam, row as unknown as Record<string, unknown>),
    onSuccess: () => {
      toast.success(t("msgLogicalUnitDeleted", { ns: NS, defaultValue: "Enregistrement supprimé." }));
      const currentPage = gridStateHandlers.getCurrentPage();
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(currentPage), U2000GridStateEnumPaginationDirection.NEXTPAGE);
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const applyPeriodCriteria = useCallback(
    (preset: U3129PeriodPreset) => {
      const periodCol = searchStateHandlers.getColumns().find((c) => c.name === "periodDate");
      if (!periodCol) return;
      const equalsCond = {
        name: "equals" as const,
        label: t("lblCndEquals", { ns: "common", defaultValue: "Égal" }),
        isRequiredValue: true,
      };
      if (preset === "all_weeks") {
        searchStateHandlers.deleteCriterias("periodDate");
        traceU3129Search("applyPeriodCriteria après delete (état peut être stale jusqu’au prochain render)", {
          preset,
          criterias: searchStateHandlers.getCriterias().map((c) => ({
            field: c.field.name,
            op: c.condition.name,
            value: c.value,
          })),
        });
        return;
      }
      const iso = preset === "current_week" ? getCurrentWeekMondayIsoLocal() : getPreviousWeekMondayIsoLocal();
      searchStateHandlers.addOrUpdateCriterias(periodCol, equalsCond, iso);
      traceU3129Search("applyPeriodCriteria après addOrUpdate (état peut être stale jusqu’au prochain render)", {
        preset,
        iso,
        criterias: searchStateHandlers.getCriterias().map((c) => ({
          field: c.field.name,
          op: c.condition.name,
          value: c.value,
        })),
      });
    },
    [searchStateHandlers, t],
  );

  useEffect(() => {
    if (columnCount === 0) return;
    if (periodPresetInitialized) return;
    const saved = searchStateHandlers.getSavedFilters();
    if (saved?.some((f) => f.isDefaultFilter)) {
      setPeriodPresetInitialized(true);
      return;
    }
    const hasPeriod = searchStateHandlers.getCriterias().some((c) => c.field.name === "periodDate");
    if (hasPeriod) {
      setPeriodPresetInitialized(true);
      return;
    }
    const presetFromInit = initLU.defaultPeriodPreset ?? "current_week";
    applyPeriodCriteria(presetFromInit);
    setPeriodPreset(presetFromInit);
    setPeriodPresetInitialized(true);
  }, [applyPeriodCriteria, columnCount, initLU.defaultPeriodPreset, periodPresetInitialized, searchStateHandlers, setPeriodPreset, setPeriodPresetInitialized]);

  const runSearchPage1 = useCallback(() => {
    const param = searchStateHandlers.extractSearchParameter();
    traceU3129Search("runSearchPage1.extractSearchParameter (doit aligner UI critères)", {
      criterias: param.criterias,
      fields: param.fields?.length,
      sortedFields: param.sortedFields?.length,
    });
    handleSearch.handleSearch(param, gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
  }, [gridStateHandlers, handleSearch, searchStateHandlers]);

  /** Toujours invoquer la dernière runSearchPage1 après setTimeout : sinon closure sur d’anciens searchStateHandlers (payload obsolète). */
  const runSearchPage1Ref = useRef(runSearchPage1);
  runSearchPage1Ref.current = runSearchPage1;

  // Recherche initiale : attend que le critère période soit RÉELLEMENT dans le state (pas juste dispatché)
  // avant de lancer la recherche. searchStateHandlers change à chaque render → l'effet réévalue
  // jusqu'à ce que getCriterias() contienne le critère, puis didRunInitialListSearchRef bloque les suivants.
  useEffect(() => {
    if (columnCount === 0) return;
    if (!periodPresetInitialized) return;
    if (!employeeId?.trim()) return;
    if (didRunInitialListSearchRef.current) return;

    if (periodPreset !== "all_weeks") {
      const criterias = searchStateHandlers.getCriterias();
      if (!criterias.some((c) => c.field.name === "periodDate")) return;
    }

    didRunInitialListSearchRef.current = true;
    const param = searchStateHandlers.extractSearchParameter();
    traceU3129Search("autoInitialSearch (critères vérifiés dans le state)", { criterias: param.criterias });
    handleSearch.handleSearch(param, gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnCount, employeeId, searchStateHandlers, periodPreset, periodPresetInitialized]);

  const onPeriodPresetChange = useCallback(
    (next: U3129PeriodPreset) => {
      traceU3129Search("onPeriodPresetChange.clic", { next, employeeId });
      setPeriodPreset(next);
      applyPeriodCriteria(next);
      // Dispatch React : attendre le render du provider pour que extractSearchParameter lise le bon searchState.
      // + ref : ne pas réutiliser une runSearchPage1 capturée avant la mise à jour du contexte.
      if (employeeId?.trim()) {
        window.setTimeout(() => {
          traceU3129Search("onPeriodPresetChange.setTimeout0 → runSearchPage1Ref.current()", null);
          runSearchPage1Ref.current();
        }, 0);
      }
    },
    [applyPeriodCriteria, employeeId, setPeriodPreset],
  );

  const onShowAllEmployeesChange = useCallback(
    (checked: boolean) => {
      setShowAllEmployees(checked);
      if (checked || employeeId?.trim()) {
        window.setTimeout(() => {
          traceU3129Search("onShowAllEmployeesChange.setTimeout0 → runSearchPage1Ref.current()", { checked });
          runSearchPage1Ref.current();
        }, 0);
      }
    },
    [employeeId, setShowAllEmployees],
  );

  const periodPresetCollapsedSlot = useMemo(
    () => (
      <>
        {(
          [
            { id: "current_week" as const, labelKey: "presetPeriodCurrent" },
            { id: "previous_week" as const, labelKey: "presetPeriodPrevious" },
            { id: "all_weeks" as const, labelKey: "presetPeriodAll" },
          ] as const
        ).map(({ id, labelKey }, idx, arr) => {
          const selected = periodPreset === id;
          return (
            <React.Fragment key={id}>
              <button
                type="button"
                onClick={() => onPeriodPresetChange(id)}
                aria-pressed={selected}
                className={`shrink-0 rounded-lg border border-transparent px-3 py-1.5 text-sm transition-all duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 ${
                  selected ? "border-blue-200 bg-blue-50 font-semibold text-blue-900 shadow-xs" : "font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                {t(labelKey, { ns: NS })}
              </button>
              {idx < arr.length - 1 && <div className="mx-2 h-4 w-px shrink-0 bg-gray-300" aria-hidden />}
            </React.Fragment>
          );
        })}
        {canSearchOtherEmployees && (
          <>
            <div className="mx-2 h-4 w-px shrink-0 bg-gray-300" aria-hidden />
            <label htmlFor="u3129-show-all-employees" className="inline-flex shrink-0 select-none items-center gap-2 px-1 text-sm font-medium text-gray-700">
              <input
                id="u3129-show-all-employees"
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded-sm border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500/30"
                checked={showAllEmployees}
                onChange={(e) => onShowAllEmployeesChange(e.target.checked)}
              />
              <span>{t("lblShowAllEmployees", { ns: NS, defaultValue: "Afficher tous les employés" })}</span>
            </label>
          </>
        )}
      </>
    ),
    [canSearchOtherEmployees, onPeriodPresetChange, onShowAllEmployeesChange, periodPreset, showAllEmployees, t],
  );

  const handlerActions: U2000TableHandlers<U3129Timesheet> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3129Timesheet) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      if (currentIdx >= 0) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`/U3129/${row.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3129");
      setAddDialogOpen(true);
    },
    onModify: () => {
      const selected = gridStateHandlers.getSelectedRows();
      if (selected.length !== 1) return;
      if (!isU3129TimesheetOpen(selected[0])) {
        toast.error(t("msgTimesheetMutateRequiresOpen", { ns: NS, defaultValue: "Cette action n’est autorisée que pour une feuille au statut Ouvert." }));
        return;
      }
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3129");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === selected[0].id);
      if (currentIdx >= 0) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`/U3129/${selected[0].id}`);
    },
    onDelete: () => {
      const selected = gridStateHandlers.getSelectedRows();
      if (selected.length === 0) return;
      if (!u3129SelectedRowsAllOpen(selected)) {
        toast.error(t("msgTimesheetMutateRequiresOpen", { ns: NS, defaultValue: "Cette action n’est autorisée que pour une feuille au statut Ouvert." }));
        return;
      }
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {},
  };

  // Employé via initLU ; première recherche lancée automatiquement (effet autoInitialSearch). Ensuite : Rechercher ou changement de préréglage.

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
      <U3129AddTimesheetDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onConfirm={(periodDate, selectedEmployee) => {
          setAddDialogOpen(false);
          if (selectedEmployee?.id) {
            setEmployeeId(selectedEmployee.id);
          }
          navigate("/U3129/create", { state: { periodDate, employee: selectedEmployee ?? null } });
        }}
        employee={employee}
      />
      <U3129TimesheetDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      {/* hideSearch : true = barre compacte ; false = panneau Critères/Tri/Colonnes ouvert (initLU) */}
      <U2000BasicTable<U3129Timesheet>
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        isRowSelectable={(row) => row.status === "Open"}
        label={t("lblTimesheetList", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        actionsButtons={
          <U3129TimesheetListToolBar
            gridStateHandlers={gridStateHandlers as unknown as U2000GridStateHandlers<Record<string, unknown>>}
            handlerActions={handlerActions as unknown as U2000TableHandlers<Record<string, unknown>>}
          />
        }
        handlerActions={handlerActions}
        allowRowSelection={true}
        hideSearchPanel={initLU.hideSearch}
        searchCollapsedLeadingSlot={periodPresetCollapsedSlot}
        showCollapsedCriteriaChips={false}
      />
    </div>
  );
};

export default U3129TimesheetList;
